import { Grading, InfoOutlined } from '@mui/icons-material';
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Stack,
    Switch,
    Typography,
} from '@mui/material';
import Button from 'domains/core/components/Button';
import AssessmentDropdown from 'domains/content/components/Bee/AssessmentDialog/AssessmentDropdown';
import { useEffect, useState } from 'react';
import { Assessment } from 'hooks/queries/useAssessments';
import { AutocompleteOption } from 'domains/core/components/Autocomplete/Autocomplete';
import {
    ContentTypeForComparison,
    isTemplate,
    Template,
    SMS,
    CommunicationType,
    ApplicationAccessType,
} from 'domains/content/types';
import { ContentStatus } from 'pages/ContentPage/ContentTable/ContentStatusText';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useCreateTemplate from 'hooks/mutations/useCreateTemplate';
import useUpdateTemplate from 'hooks/mutations/useUpdateTemplate';
import useQueryParams from 'hooks/useQueryParams';
import { Query, useQueryClient } from 'react-query';
import { useHistory } from 'react-router';
import { getContentCreatePath } from 'pages/ContentPage/ContentTable/utils';
import SuccessSnackbar from 'domains/core/components/Snackbars/SuccessSnackbar';
import Alert from 'domains/core/components/Alert';
import InputLabel from 'domains/core/components/InputLabel';

export type ContentWithAssessment = Template | SMS;

export type Props = {
    applicationsAccess?: ApplicationAccessType[];
    content: ContentWithAssessment;
    communicationType?: CommunicationType;
    isDialogVisible: boolean;
    onAssessmentPreview: () => void;
    setIsDialogVisible: (isDialogVisible: boolean) => void;
    onAssessmentTagInsert?: (
        assessmentId: string,
        assessmentName: string,
        isConsentRequired: boolean,
        tagToInsert: string
    ) => void;
    setBeeEditorIsNoRestart?: (isNoRestart: boolean) => void;
    setContent?: (data: Partial<ContentWithAssessment>) => void;
    onStatusChange?: (status: ContentStatus) => void;
};

const extractAssessmentData = (content: ContentWithAssessment) => {
    if (!content) return { id: '', name: '', consent: true };

    return {
        id: content?.assessmentId || '',
        name: content?.assessmentName || '',
        consent: content?.assessmentConsentRequired ?? true,
    };
};

const AssessmentDialog = ({
    applicationsAccess,
    content,
    communicationType,
    isDialogVisible,
    onAssessmentPreview,
    setIsDialogVisible,
    onAssessmentTagInsert,
    setBeeEditorIsNoRestart,
    setContent,
    onStatusChange,
}: Props) => {
    const [assessmentInputValue, setAssessmentInputValue] = useState('');
    const [assessmentConsent, setAssessmentConsent] = useState<boolean>(true);
    const [initialAssessment, setInitialAssessment] = useState<AutocompleteOption<Assessment>>(null);
    const [initialAssessmentConsent, setInitialAssessmentConsent] = useState<boolean>(true);
    const [selectedAssessment, setSelectedAssessment] = useState<AutocompleteOption<Assessment>>(null);
    const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
    const [contentType, setContentType] = useState<'email' | 'sms'>('email');

    useEffect(() => {
        if (isDialogVisible && content) {
            const isSms = !isTemplate(content) && 'content' in content;
            setContentType(isSms ? 'sms' : 'email');

            const { id, name, consent } = extractAssessmentData(content);

            const assessment =
                id && name
                    ? {
                          label: name,
                          value: { id, name },
                      }
                    : null;

            setSelectedAssessment(assessment);
            setInitialAssessment(assessment);
            setAssessmentConsent(consent);
            setInitialAssessmentConsent(consent);
        }
    }, [content, isDialogVisible]);

    const query = useQueryParams();
    const templateId = query.get('id');
    const parentFolderId = Number(query.get('parentFolderId'));
    const templateBeeJson = isTemplate(content) ? content.beeJson : JSON.stringify(content);
    const queryClient = useQueryClient();
    const history = useHistory();
    const { contentDraft: hasContentDraft } = useFlags();
    const isSaveDisabled = selectedAssessment === initialAssessment && initialAssessmentConsent === assessmentConsent;
    const isAssessmentBeingAdded =
        selectedAssessment && (!initialAssessment || selectedAssessment !== initialAssessment);
    const isAssessmentRemovalAttempt = !selectedAssessment && !!initialAssessment;

    const contentTypeLabel = contentType === 'sms' ? 'SMS' : 'email';
    const contentTypeInstructions =
        contentType === 'sms'
            ? 'The assessment URL will be inserted at your cursor position in the message.'
            : 'Copy the merge tag and paste it into a prominent CTA button to encourage completions.';

    const saveButtonLabel = isAssessmentRemovalAttempt
        ? 'Save'
        : contentType === 'sms'
        ? 'Save & Insert Tag'
        : 'Save & Copy Tag';

    const successMessage =
        contentType === 'sms'
            ? isAssessmentRemovalAttempt
                ? 'Assessment tag removed.'
                : 'Assessment tag inserted.'
            : 'Assessment tag copied.';

    useEffect(() => {
        if (showSuccessSnackbar) {
            // Reset success states after a delay
            const timer = setTimeout(() => {
                setShowSuccessSnackbar(false);
            }, 6000); // Snackbar display duration

            return () => clearTimeout(timer);
        }
    }, [showSuccessSnackbar]);

    const onSuccess = async (data: any) => {
        queryClient.invalidateQueries({
            // When we update a template, the cached network calls will be stale for the updated templates, so we need to invalidate the cached GET requests.
            predicate: (query: Query) => query.queryKey[0] === 'template-id',
        });

        if (templateId === 'default') {
            setBeeEditorIsNoRestart?.(true);
            history.push(
                getContentCreatePath({
                    contentType: contentType === 'sms' ? ContentTypeForComparison.SMS : ContentTypeForComparison.EMAIL,
                    id: data.id,
                    parentFolderId,
                })
            );
        }

        if (!isAssessmentRemovalAttempt && contentType === 'email') {
            await navigator.clipboard.writeText('{{assessment_url}}');
            setShowSuccessSnackbar(true);
        }
    };

    const { mutate: saveTemplate } = useCreateTemplate({ onSuccess });

    const { mutate: updateTemplate } = useUpdateTemplate(templateId, {
        onSuccess,
    });

    const onChangeAssessment = (e: any, option: AutocompleteOption<Assessment>) => {
        if (option) {
            setSelectedAssessment(option);
        } else {
            setSelectedAssessment(null);
            setAssessmentConsent(true);
        }
    };

    const handleSubmit = () => {
        if (contentType === 'sms') {
            if (isAssessmentRemovalAttempt) {
                // Get the original content
                const currentContent = content && 'content' in content ? content.content || '' : '';

                // Remove all instances of the assessment URL tag from content
                const cleanedContent = currentContent.replace(/{{assessment_url}}/g, '');

                // Update both assessment data AND content
                setContent?.({
                    assessmentId: '',
                    assessmentName: '',
                    assessmentConsentRequired: true,
                    content: cleanedContent,
                    ...(hasContentDraft && { status: ContentStatus.DRAFT }),
                });

                setShowSuccessSnackbar(true);
                setIsDialogVisible(false);
                return;
            }

            if (selectedAssessment) {
                // Call the combined handler instead of separate functions
                onAssessmentTagInsert?.(
                    selectedAssessment.value.id,
                    selectedAssessment.value.name,
                    assessmentConsent,
                    '{{assessment_url}}'
                );

                setShowSuccessSnackbar(true);
                setIsDialogVisible(false);
                return;
            }

            // No valid changes - just close dialog
            setIsDialogVisible(false);
            return;
        }

        const sendDetails = {
            ...(isTemplate(content) && content.preHeader && { preHeader: content.preHeader }),
            ...(isTemplate(content) && content.senderProfileId && { senderProfileId: content.senderProfileId }),
            ...(isTemplate(content) && content.subjectLine && { subjectLine: content.subjectLine }),
        };

        const assessmentInput = {
            assessmentId: selectedAssessment ? selectedAssessment.value.id : '',
            assessmentName: selectedAssessment ? selectedAssessment.value.name : '',
            assessmentConsentRequired: assessmentConsent,
        };

        const name = isTemplate(content) ? content.name : 'Untitled';

        // When assessment is added, set status to DRAFT immediately in local state
        if (isAssessmentBeingAdded && hasContentDraft && onStatusChange) {
            onStatusChange(ContentStatus.DRAFT);
        }

        const mutationInput = {
            beeJson: templateBeeJson,
            ...(applicationsAccess && { applicationsAccess: applicationsAccess.join(',') as ApplicationAccessType }),
            ...(communicationType && { communicationType }),
            ...{ templateName: name },
            ...(parentFolderId && { parentFolderId }),
            ...sendDetails,
            ...assessmentInput,
            ...(hasContentDraft && { status: ContentStatus.DRAFT }),
        };

        if (templateId === 'default') {
            saveTemplate(mutationInput);
        } else {
            updateTemplate({ id: templateId, ...mutationInput });
        }

        setIsDialogVisible(false);
    };

    const handleClose = () => {
        setSelectedAssessment(initialAssessment);
        setIsDialogVisible(false);
    };

    return (
        <>
            {showSuccessSnackbar && <SuccessSnackbar successMessage={successMessage} />}
            <Dialog
                maxWidth="sm"
                fullWidth
                open={isDialogVisible}
                onClose={handleClose}
                TransitionProps={{
                    onExited: () => {},
                }}
            >
                <DialogTitle>
                    <Stack alignItems="center" direction="row" spacing={1}>
                        <Grading sx={{ paddingTop: '2px' }} />
                        <Typography variant="h6">select assessment</Typography>
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ padding: '16px 32px' }}>
                    <DialogContentText mb={3} mt={1}>
                        Select an assessment to include in your {contentTypeLabel}. {contentTypeInstructions}
                    </DialogContentText>

                    <AssessmentDropdown
                        onAssessmentPreview={onAssessmentPreview}
                        onChange={onChangeAssessment}
                        // For some reason the signature of this prop is wrong and does not match the docs.
                        // @ts-ignore
                        onInputChange={(e: React.SyntheticEvent, inputValue: string, reason: string) => {
                            if (reason === 'clear') {
                                setAssessmentInputValue('');
                                setSelectedAssessment(null);
                            } else {
                                setAssessmentInputValue(inputValue);
                            }
                        }}
                        isPreviewVisible={true}
                        textfieldInput={assessmentInputValue}
                        value={selectedAssessment}
                    />
                    <FormControlLabel
                        control={
                            <Box my={1}>
                                <Switch
                                    color={assessmentConsent ? 'secondary' : 'default'}
                                    size="small"
                                    checked={assessmentConsent}
                                    disabled={!selectedAssessment}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        setAssessmentConsent(e.target.checked);
                                    }}
                                    sx={{ mx: 1 }}
                                />
                            </Box>
                        }
                        label={
                            <InputLabel
                                label={
                                    <Typography variant="overline" color={!selectedAssessment && 'text.disabled'}>
                                        Include Consent Form
                                    </Typography>
                                }
                                labelIcon={<InfoOutlined color={'inherit'} fontSize="inherit" />}
                                labelIconTooltipText={
                                    'Collect consent and inform patients of health data usage at the start of your assessment'
                                }
                            />
                        }
                        sx={{ cursor: 'pointer' }}
                    />
                    {contentType === 'email' && isAssessmentRemovalAttempt && (
                        <Alert elevation={0} severity="warning" variant="standard">
                            You are choosing to remove assessment. Please manually remove all assessment tags from the
                            content or URLs once this decision is saved.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant="text" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        data-testid="assessment-save-and-copy"
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isSaveDisabled}
                    >
                        {saveButtonLabel}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
export default AssessmentDialog;
